import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AppBar, Avatar, Box, Button, Chip, CircularProgress, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControlLabel, IconButton, InputAdornment, LinearProgress, ListItemIcon, Menu, MenuItem, Snackbar, Switch, TextField, Toolbar, Tooltip,
    Typography, useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import DisboxFileManager from './disbox-file-manager';
import PublicLibrary from './public-library';
import VideoPlayer from './VideoPlayer';
import { botJson, fileUrl, getAccount, hasValidSession, onAuthFailure, refreshSessionIfNeeded, clearLocalSession, setPublicMode } from './api.js';
import { getContinueWatching, loadProgress, formatTime } from './watch-progress';
import {
    buildLibrary, canUseAnime, episodeProgress, episodeTitle, findAnimeRoot, findEpisode, lastWatchedAt, nextUpEpisode, setAnimeRoot,
} from './anime-library';
import { linkId, matchesLink } from './link-id';
import { animeLoginUrl, animeLogout, deleteAnimeAccount, fetchAnimeAccount, setAnimeFavorite } from './anime-account';
import {
    clearAnimeInfoCache, fetchAnimeInfo, FORMAT_LABELS, getSearchOverride, isAnilistEnabled, setAnilistEnabled, setSearchOverride, STATUS_LABELS,
} from './anilist';

const ACCENT = '#ff7a1a';

const theme = createTheme({
    shape: { borderRadius: 10 },
    palette: {
        mode: 'dark',
        primary: { main: ACCENT, contrastText: '#000' },
        background: { default: '#0e0e12', paper: '#18181f' },
    },
    typography: { button: { textTransform: 'none', fontWeight: 700 } },
    components: {
        // Scrollbar bawaan browser (putih) disamakan dengan tema gelap.
        MuiCssBaseline: {
            styleOverrides: {
                html: { colorScheme: 'dark' },
                '*': { scrollbarColor: 'rgba(255,255,255,.22) transparent' },
                '*::-webkit-scrollbar': { width: 8, height: 8 },
                '*::-webkit-scrollbar-track': { background: 'transparent' },
                '*::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,.22)', borderRadius: 8 },
                '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,.35)' },
            },
        },
        MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
        MuiButton: { styleOverrides: { root: { borderRadius: 10 } } },
    },
});

const POSTER_FILE = /^(poster|cover|folder)\.(jpe?g|png|webp)$/i;

function cssUrl(url) {
    return url ? `url("${String(url).replace(/"/g, '%22')}")` : 'none';
}

// Gambar poster seri: file poster/cover/folder.jpg di folder seri (kalau ada), lalu poster AniList.
function useArtwork(fileManager, series, info) {
    return useMemo(() => {
        let localPoster = null;
        if (series && fileManager) {
            try {
                const folder = fileManager.getFile(series.path, false);
                const name = folder && folder.children ? Object.keys(folder.children).find((n) => POSTER_FILE.test(n)) : null;
                if (name) localPoster = fileUrl('stream', fileManager.userId, `${series.path}/${name}`);
            } catch {
                // Seri berupa satu file film: tidak ada folder.
            }
        }
        return {
            poster: localPoster || (info && info.cover) || null,
            banner: (info && info.banner) || localPoster || (info && info.cover) || null,
            color: (info && info.color) || ACCENT,
        };
    }, [fileManager, series, info]);
}

function displayTitle(series, info) {
    return (info && info.title) || series.title;
}

// ---------------------------------------------------------------- kartu

function PosterCard({ fileManager, series, info, onOpen }) {
    const art = useArtwork(fileManager, series, info);
    const next = nextUpEpisode(series);
    const watchedCount = series.episodes.filter((e) => e.kind === 'episode' && episodeProgress(e).watched).length;
    return (
        <Box onClick={onOpen} sx={{ cursor: 'pointer', minWidth: 0, '&:hover .poster': { transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${art.color}55` } }}>
            <Box
                className="poster"
                sx={{
                    position: 'relative',
                    aspectRatio: '2 / 3',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#23232c',
                    backgroundImage: cssUrl(art.poster),
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform .2s, box-shadow .2s',
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                {!art.poster && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5, textAlign: 'center', background: `linear-gradient(160deg, ${art.color}55, #18181f)` }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{displayTitle(series, info)}</Typography>
                    </Box>
                )}
                {info && info.score && (
                    <Chip size="small" icon={<StarIcon sx={{ fontSize: 14 }} />} label={(info.score / 10).toFixed(1)}
                        sx={{ position: 'absolute', top: 6, left: 6, height: 22, bgcolor: 'rgba(0,0,0,.7)', color: '#ffd166', '& .MuiChip-icon': { color: '#ffd166' } }} />
                )}
                {series.episodeCount > 0 && (
                    <Chip size="small" label={`${series.episodeCount} Ep`} sx={{ position: 'absolute', top: 6, right: 6, height: 22, bgcolor: ACCENT, color: '#000', fontWeight: 700 }} />
                )}
                {watchedCount > 0 && (
                    <Box sx={{ position: 'relative', width: '100%', p: 0.75, background: 'linear-gradient(transparent, rgba(0,0,0,.85))' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {next.finished ? 'Selesai ditonton' : `Lanjut: ${episodeTitle(next.episode)}`}
                        </Typography>
                        <LinearProgress variant="determinate" value={series.episodeCount ? (watchedCount / series.episodeCount) * 100 : 0} sx={{ height: 3, borderRadius: 2, mt: 0.25 }} />
                    </Box>
                )}
            </Box>
            <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {displayTitle(series, info)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {[info && info.year, info && FORMAT_LABELS[info.format], `${series.seasons.length} bagian`].filter(Boolean).join(' · ')}
            </Typography>
        </Box>
    );
}

function ContinueCard({ fileManager, entry, info, onPlay }) {
    const art = useArtwork(fileManager, entry.series, info);
    const progress = episodeProgress(entry.episode);
    return (
        <Box onClick={onPlay} sx={{ cursor: 'pointer', width: { xs: 240, sm: 280 }, flexShrink: 0, '&:hover .thumb': { outline: `2px solid ${ACCENT}` } }}>
            <Box className="thumb" sx={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 2, overflow: 'hidden', bgcolor: '#23232c', backgroundImage: cssUrl(art.banner), backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 40%, rgba(0,0,0,.85))' }} />
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PlayArrowIcon />
                    </Box>
                </Box>
                <Typography variant="caption" sx={{ position: 'absolute', right: 8, bottom: 8, fontWeight: 600 }}>
                    {formatTime(progress.position)}
                </Typography>
                <LinearProgress variant="determinate" value={progress.percent} sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 4 }} />
            </Box>
            <Typography variant="body2" noWrap sx={{ mt: 0.75, fontWeight: 700 }}>{displayTitle(entry.series, info)}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
                {entry.episode.seasonLabel} · {episodeTitle(entry.episode)}
            </Typography>
        </Box>
    );
}

function EpisodeCard({ episode, active, color, onPlay }) {
    const progress = episodeProgress(episode);
    const label = episode.kind === 'episode' ? String(episode.number).padStart(2, '0') : episode.kind === 'movie' ? 'MOVIE' : episode.kind === 'extra' ? 'EX' : episode.tag === 'OVA' ? 'OVA' : 'SP';
    return (
        <Box
            onClick={onPlay}
            sx={{
                display: 'flex', gap: 1.25, p: 1, borderRadius: 2, cursor: 'pointer', alignItems: 'center', minWidth: 0,
                bgcolor: active ? `${ACCENT}22` : 'background.paper',
                border: '1px solid', borderColor: active ? ACCENT : 'transparent',
                '&:hover': { borderColor: active ? ACCENT : 'rgba(255,255,255,.2)' },
            }}
        >
            <Box sx={{ position: 'relative', width: 92, flexShrink: 0, aspectRatio: '16 / 9', borderRadius: 1.5, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${color}66, #23232c)` }}>
                <Typography sx={{ fontWeight: 800, fontSize: label.length > 3 ? 15 : 22, opacity: 0.9 }}>{label}</Typography>
                {progress.percent > 0 && (
                    <LinearProgress variant="determinate" value={progress.percent} sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3 }} />
                )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{episodeTitle(episode)}</Typography>
                    {progress.watched && <CheckCircleIcon sx={{ fontSize: 16, color: '#4ade80' }} />}
                    {active && <Chip label="Diputar" size="small" color="primary" sx={{ height: 18, fontSize: 11, ml: 0.5 }} />}
                </Box>
                {/* Nama file sengaja tidak ditampilkan (sering berisi nama situs asal unduhan). */}
                {progress.percent > 0 && !progress.watched && (
                    <Typography variant="caption" sx={{ color: ACCENT }}>Berhenti di {formatTime(progress.position)}</Typography>
                )}
            </Box>
        </Box>
    );
}

// Baris kartu yang digeser ke samping: tanpa scrollbar, dengan tombol panah
// di desktop (muncul saat kursor di atas baris). Di HP cukup digeser jari.
function ScrollRow({ children }) {
    const ref = useRef(null);
    const [edges, setEdges] = useState({ left: false, right: false });

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const left = el.scrollLeft > 4;
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
        setEdges((cur) => (cur.left === left && cur.right === right ? cur : { left, right }));
    }, []);

    useEffect(() => {
        update();
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [update, children]);

    const scroll = (dir) => {
        const el = ref.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
    };

    const arrow = (dir) => (
        <Box
            className="row-arrow"
            onClick={() => scroll(dir)}
            sx={{
                position: 'absolute', top: 0, bottom: 8, [dir < 0 ? 'left' : 'right']: 0, width: 56, zIndex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                opacity: 0, transition: 'opacity .2s',
                background: `linear-gradient(${dir < 0 ? '90deg' : '270deg'}, rgba(14,14,18,.95), rgba(14,14,18,0))`,
                '@media (hover: none)': { display: 'none' },
            }}
        >
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(24,24,31,.9)', border: '1px solid rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: ACCENT, color: '#000' } }}>
                {dir < 0 ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ position: 'relative', '&:hover .row-arrow': { opacity: 1 } }}>
            {edges.left && arrow(-1)}
            <Box
                ref={ref}
                onScroll={update}
                sx={{
                    display: 'flex', gap: 2, overflowX: 'auto', pb: 1,
                    scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {children}
            </Box>
            {edges.right && arrow(1)}
        </Box>
    );
}

function SeasonTabs({ seasons, value, onChange }) {
    if (seasons.length < 2) return null;
    return (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1.5, scrollbarWidth: 'thin' }}>
            {seasons.map((season) => (
                <Chip
                    key={season.id}
                    label={`${season.label} (${season.episodes.length})`}
                    onClick={() => onChange(season.id)}
                    color={season.id === value ? 'primary' : 'default'}
                    variant={season.id === value ? 'filled' : 'outlined'}
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                />
            ))}
        </Box>
    );
}

// ---------------------------------------------------------------- halaman

// Tombol favorit: hanya ada kalau penonton sudah login (favorite = null kalau belum).
function FavoriteButton({ favorite, size = 'large' }) {
    if (!favorite) return null;
    return (
        <Button variant="outlined" color="inherit" size={size} onClick={favorite.onToggle}
            startIcon={favorite.active ? <FavoriteIcon sx={{ color: '#ff4d6d' }} /> : <FavoriteBorderIcon />}>
            {favorite.active ? 'Favorit' : 'Tambah ke favorit'}
        </Button>
    );
}

function Hero({ fileManager, series, info, onPlay, onDetail, compact, favorite }) {
    const art = useArtwork(fileManager, series, info);
    const next = nextUpEpisode(series);
    const [expanded, setExpanded] = useState(false);
    const meta = [
        info && info.year,
        info && FORMAT_LABELS[info.format],
        info && STATUS_LABELS[info.status],
        series.episodeCount > 0 && `${series.episodeCount} episode tersedia`,
    ].filter(Boolean);
    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: { xs: 300, md: 420 }, display: 'flex', alignItems: 'flex-end' }}>
            <Box sx={{ position: 'absolute', inset: 0, backgroundImage: cssUrl(art.banner), backgroundSize: 'cover', backgroundPosition: 'center 25%', filter: art.banner === art.poster ? 'blur(18px) brightness(.6)' : 'none', transform: art.banner === art.poster ? 'scale(1.1)' : 'none' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, #0e0e12 4%, rgba(14,14,18,.75) 45%, rgba(14,14,18,.2)), linear-gradient(90deg, rgba(14,14,18,.85), transparent 70%)' }} />
            <Box sx={{ position: 'relative', display: 'flex', gap: 3, alignItems: 'flex-end', px: { xs: 2, md: 5 }, pb: { xs: 2.5, md: 4 }, pt: 10, width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {art.poster && (
                    <Box sx={{ display: { xs: 'none', sm: 'block' }, width: { sm: 150, md: 200 }, flexShrink: 0, aspectRatio: '2 / 3', borderRadius: 2, backgroundImage: cssUrl(art.poster), backgroundSize: 'cover', backgroundPosition: 'center', boxShadow: '0 10px 30px rgba(0,0,0,.6)' }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 40 }, lineHeight: 1.1, textShadow: '0 2px 12px rgba(0,0,0,.6)' }}>
                        {displayTitle(series, info)}
                    </Typography>
                    {info && info.titleEnglish && info.titleEnglish !== info.title && (
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>{info.titleEnglish}</Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1.25 }}>
                        {info && info.score && (
                            <Chip size="small" icon={<StarIcon sx={{ fontSize: 16 }} />} label={(info.score / 10).toFixed(1)} sx={{ bgcolor: 'rgba(0,0,0,.5)', color: '#ffd166', '& .MuiChip-icon': { color: '#ffd166' } }} />
                        )}
                        {meta.map((m) => <Typography key={m} variant="body2" sx={{ opacity: 0.85 }}>{m}</Typography>).reduce((acc, el, i) => (i ? [...acc, <span key={`d${i}`} style={{ opacity: 0.4 }}>•</span>, el] : [el]), [])}
                    </Box>
                    {info && info.genres.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                            {info.genres.slice(0, 5).map((g) => <Chip key={g} label={g} size="small" variant="outlined" />)}
                        </Box>
                    )}
                    {info && info.description && (
                        <Typography
                            variant="body2"
                            onClick={() => setExpanded((v) => !v)}
                            sx={{
                                mt: 1.5, maxWidth: 760, opacity: 0.85, whiteSpace: 'pre-line', cursor: 'pointer',
                                ...(expanded ? {} : { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: compact ? 3 : 4, WebkitBoxOrient: 'vertical' }),
                            }}
                        >
                            {info.description}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        <Button variant="contained" size="large" startIcon={<PlayArrowIcon />} onClick={() => onPlay(next.episode)}>
                            {next.resume ? `Lanjutkan ${episodeTitle(next.episode)}` : next.finished ? 'Tonton lagi' : series.episodes.length > 1 ? `Tonton ${episodeTitle(next.episode)}` : 'Tonton'}
                        </Button>
                        {onDetail && (
                            <Button variant="outlined" color="inherit" size="large" startIcon={<InfoOutlinedIcon />} onClick={onDetail}>Detail</Button>
                        )}
                        <FavoriteButton favorite={favorite} />
                    </Box>
                    {next.resume && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {next.episode.seasonLabel} · berhenti di {formatTime(episodeProgress(next.episode).position)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

function HomeView({ fileManager, library, infos, query, onOpenSeries, onPlay, favoriteOf, favoriteSeries }) {
    const continueEntries = useMemo(() => {
        const entries = [];
        for (const item of getContinueWatching(40)) {
            const found = findEpisode(library, item.path);
            if (found && !entries.some((e) => e.series.id === found.series.id)) entries.push(found);
        }
        return entries;
    }, [library]);

    const featured = useMemo(() => {
        if (library.length === 0) return null;
        const watched = [...library].sort((a, b) => lastWatchedAt(b) - lastWatchedAt(a));
        if (lastWatchedAt(watched[0]) > 0) return watched[0];
        return [...library].sort((a, b) => b.updatedAt - a.updatedAt)[0];
    }, [library]);

    const text = query.trim().toLowerCase();
    const filtered = text
        ? library.filter((s) => [s.title, s.folderName, infos[s.id] && infos[s.id].title, infos[s.id] && infos[s.id].titleEnglish]
            .filter(Boolean).some((t) => t.toLowerCase().includes(text)))
        : library;
    const recent = [...library].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 12);

    return (
        <Box sx={{ pb: 6 }}>
            {!text && featured && (
                <Hero fileManager={fileManager} series={featured} info={infos[featured.id]} onPlay={(ep) => onPlay(ep, featured)} onDetail={() => onOpenSeries(featured)} compact favorite={favoriteOf(featured)} />
            )}
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 5 }, mt: text ? 3 : 1 }}>
                {!text && continueEntries.length > 0 && (
                    <Section title="Lanjutkan menonton">
                        <ScrollRow>
                            {continueEntries.map((entry) => (
                                <ContinueCard key={entry.episode.path} fileManager={fileManager} entry={entry} info={infos[entry.series.id]} onPlay={() => onPlay(entry.episode, entry.series)} />
                            ))}
                        </ScrollRow>
                    </Section>
                )}
                {!text && favoriteSeries.length > 0 && (
                    <Section title="Favorit saya">
                        <ScrollRow>
                            {favoriteSeries.map((series) => (
                                <Box key={series.id} sx={{ width: { xs: 120, sm: 150 }, flexShrink: 0 }}>
                                    <PosterCard fileManager={fileManager} series={series} info={infos[series.id]} onOpen={() => onOpenSeries(series)} />
                                </Box>
                            ))}
                        </ScrollRow>
                    </Section>
                )}
                {!text && library.length > 6 && (
                    <Section title="Baru ditambahkan">
                        <ScrollRow>
                            {recent.map((series) => (
                                <Box key={series.id} sx={{ width: { xs: 120, sm: 150 }, flexShrink: 0 }}>
                                    <PosterCard fileManager={fileManager} series={series} info={infos[series.id]} onOpen={() => onOpenSeries(series)} />
                                </Box>
                            ))}
                        </ScrollRow>
                    </Section>
                )}
                <Section title={text ? `Hasil pencarian "${query.trim()}"` : 'Semua anime'}>
                    {filtered.length === 0 ? (
                        <Typography color="text.secondary">Tidak ada anime yang cocok.</Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gap: { xs: 1.5, sm: 2.5 }, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' } }}>
                            {filtered.map((series) => (
                                <PosterCard key={series.id} fileManager={fileManager} series={series} info={infos[series.id]} onOpen={() => onOpenSeries(series)} />
                            ))}
                        </Box>
                    )}
                </Section>
            </Box>
        </Box>
    );
}

function Section({ title, children }) {
    return (
        <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 20 }, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 4, height: 20, borderRadius: 1, bgcolor: ACCENT, display: 'inline-block' }} />
                {title}
            </Typography>
            {children}
        </Box>
    );
}

function SeriesView({ fileManager, series, info, onPlay, onEditInfo, favorite }) {
    const next = nextUpEpisode(series);
    const [seasonId, setSeasonId] = useState(() => next.episode.seasonId || series.seasons[0].id);
    const season = series.seasons.find((s) => s.id === seasonId) || series.seasons[0];
    const art = useArtwork(fileManager, series, info);
    return (
        <Box sx={{ pb: 6 }}>
            <Hero fileManager={fileManager} series={series} info={info} onPlay={(ep) => onPlay(ep, series)} favorite={favorite} />
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 5 } }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {onEditInfo && <Button size="small" color="inherit" startIcon={<EditIcon />} onClick={onEditInfo}>Ubah info AniList</Button>}
                    {info && info.siteUrl && (
                        <Button size="small" color="inherit" startIcon={<OpenInNewIcon />} href={info.siteUrl} target="_blank" rel="noreferrer">Buka di AniList</Button>
                    )}
                </Box>
                <Section title="Episode">
                    <SeasonTabs seasons={series.seasons} value={season.id} onChange={setSeasonId} />
                    <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
                        {season.episodes.map((episode) => (
                            <EpisodeCard key={episode.path} episode={{ ...episode, seasonId: season.id, seasonLabel: season.label }} color={art.color} onPlay={() => onPlay(episode, series)} />
                        ))}
                    </Box>
                </Section>
            </Box>
        </Box>
    );
}

function WatchView({ fileManager, series, episode, info, onPlay, onOpenSeries, isDesktop, allowDownload, favorite }) {
    const art = useArtwork(fileManager, series, info);
    const [seasonId, setSeasonId] = useState(episode.seasonId);
    useEffect(() => setSeasonId(episode.seasonId), [episode.seasonId]);
    const season = series.seasons.find((s) => s.id === seasonId) || series.seasons[0];
    // label: judul yang ditampilkan pemutar (episode berikutnya, tombol
    // sebelum/berikutnya) sebagai pengganti nama file.
    const labelOf = (e) => (series.seasons.length > 1 && e.seasonLabel ? `${e.seasonLabel} · ${episodeTitle(e)}` : episodeTitle(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const playlist = useMemo(() => series.episodes.map((e) => ({ ...e.node, path: e.path, name: e.name, label: labelOf(e) })), [series]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const row = useMemo(() => ({ ...episode.node, path: episode.path, name: episode.name, label: labelOf(episode) }), [episode]);
    const onNavigate = (target) => {
        const found = series.episodes.find((e) => e.path === target.path);
        if (found) onPlay(found, series, { replace: true });
    };
    const onDownload = (target) => {
        window.open(fileUrl('stream', fileManager.userId, target.path, { dl: 1 }), '_blank');
    };

    const episodeList = (
        <Box>
            {series.seasons.length > 1 && (
                <TextField select fullWidth size="small" label="Bagian" value={season.id} onChange={(e) => setSeasonId(e.target.value)} sx={{ mb: 1.25 }}>
                    {series.seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.label} ({s.episodes.length})</MenuItem>)}
                </TextField>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {season.episodes.map((e) => (
                    <EpisodeCard key={e.path} episode={e} active={e.path === episode.path} color={art.color} onPlay={() => onPlay({ ...e, seasonId: season.id, seasonLabel: season.label }, series, { replace: true })} />
                ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 0, md: 3 }, py: { xs: 0, md: 2.5 }, display: 'flex', gap: 3, flexDirection: isDesktop ? 'row' : 'column', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <VideoPlayer key={episode.path} fileManager={fileManager} row={row} playlist={playlist} onNavigate={onNavigate} onDownload={allowDownload ? onDownload : null} />
                <Box sx={{ px: { xs: 2, md: 0 }, mt: 2 }}>
                    <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 0.5 }}>{episode.seasonLabel}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 26 }, lineHeight: 1.2 }}>{episodeTitle(episode)}</Typography>
                    <Box onClick={() => onOpenSeries(series)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 1, cursor: 'pointer', '&:hover': { color: ACCENT } }}>
                        {art.poster && <Box sx={{ width: 32, height: 48, borderRadius: 1, backgroundImage: cssUrl(art.poster), backgroundSize: 'cover', backgroundPosition: 'center' }} />}
                        <Typography sx={{ fontWeight: 600 }}>{displayTitle(series, info)}</Typography>
                    </Box>
                    {favorite && (
                        <Box sx={{ mt: 1.5 }}><FavoriteButton favorite={favorite} size="small" /></Box>
                    )}
                </Box>
            </Box>
            <Box sx={{ width: isDesktop ? 380 : '100%', flexShrink: 0, px: { xs: 2, md: 0 }, pb: 4 }}>
                <Typography sx={{ fontWeight: 800, mb: 1.25 }}>Daftar episode</Typography>
                <Box sx={isDesktop ? { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', pr: 0.5, scrollbarWidth: 'thin' } : {}}>
                    {episodeList}
                </Box>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------- dialog

// onSave(nilai): '' = kembali otomatis (judul dari nama folder).
function InfoDialog({ series, info, initial, onClose, onSave }) {
    const [text, setText] = useState(() => initial || series.title);
    const save = () => {
        const value = text.trim();
        onSave(value && value !== series.title ? value : '');
    };
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Info AniList: {series.folderName}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Poster dan sinopsis dicari di AniList memakai judul ini. Kalau hasilnya salah, ketik judul yang lebih tepat,
                    atau angka ID dari link AniList (misalnya 114963).
                </Typography>
                <TextField autoFocus fullWidth label="Judul pencarian / ID AniList" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
                {info && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Sekarang: {info.title} (ID {info.id})</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onSave('')} color="inherit">Kembalikan otomatis</Button>
                <Button onClick={onClose} color="inherit">Batal</Button>
                <Button onClick={save} variant="contained">Simpan</Button>
            </DialogActions>
        </Dialog>
    );
}

function SettingsDialogAnime({ fileManager, root, onClose, onChanged }) {
    const folders = Object.values(fileManager.getChildren('')).filter((c) => c.type === 'directory');
    const [value, setValue] = useState(root || '');
    const [anilist, setAnilist] = useState(isAnilistEnabled());
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Setelan Anime</DialogTitle>
            <DialogContent>
                <TextField select fullWidth label="Folder anime" value={value} onChange={(e) => setValue(e.target.value)} sx={{ mt: 1 }}
                    helperText="Tiap subfolder di sini dianggap satu judul anime.">
                    {folders.map((f) => <MenuItem key={f.path} value={f.path}>{f.path}</MenuItem>)}
                </TextField>
                <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={anilist} onChange={(e) => setAnilist(e.target.checked)} />}
                    label="Ambil poster & sinopsis dari AniList" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Yang dikirim ke AniList hanya judul anime (nama folder), bukan file atau data akun.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Batal</Button>
                <Button variant="contained" onClick={() => { setAnimeRoot(value); setAnilistEnabled(anilist); onChanged(); }}>Simpan</Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------- utama

// publicMode: halaman /nonton untuk siapa saja (tanpa login). Hanya folder
// anime yang dikirim bot, hanya streaming: tanpa setelan, download, atau
// akses ke file lain (lihat public-library.js).
export default function AnimePage({ publicMode = false }) {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [fileManager, setFileManager] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [treeVersion, setTreeVersion] = useState(0);
    const [infos, setInfos] = useState({});
    const [infoVersion, setInfoVersion] = useState(0);
    const [query, setQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Padanan folder seri -> ID/judul AniList yang disimpan di bot (berlaku
    // untuk semua penonton); pilihan lokal lama di browser jadi cadangan.
    const [serverMap, setServerMap] = useState({});
    const allowed = publicMode || canUseAnime();
    const searchFor = (series) => serverMap[series.id] || getSearchOverride(series.id) || series.title;

    // ---------- akun penonton opsional (login Discord, untuk favorit) ----------
    const [account, setAccount] = useState({ canLogin: false, user: null, favorites: [] });
    const [loginOpen, setLoginOpen] = useState(false);
    const [accountMenu, setAccountMenu] = useState(null);
    const [notice, setNotice] = useState(null);
    useEffect(() => {
        if (!publicMode) return;
        fetchAnimeAccount().then(setAccount);
        const result = params.get('login');
        if (result) {
            setNotice(result === 'batal' ? 'Login dibatalkan.' : result === 'mati' ? 'Login belum diaktifkan.' : 'Login gagal, coba lagi.');
            const rest = new URLSearchParams(params);
            rest.delete('login');
            setParams(rest, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicMode]);

    const favoriteIds = account.favorites;
    const toggleFavorite = async (series) => {
        const on = !favoriteIds.includes(series.id);
        const previous = favoriteIds;
        setAccount((a) => ({ ...a, favorites: on ? [series.id, ...a.favorites] : a.favorites.filter((id) => id !== series.id) }));
        try {
            const favorites = await setAnimeFavorite(series.id, on);
            setAccount((a) => ({ ...a, favorites }));
            setNotice(on ? 'Ditambahkan ke favorit.' : 'Dihapus dari favorit.');
        } catch (error) {
            setAccount((a) => ({ ...a, favorites: previous }));
            setNotice(`Gagal menyimpan favorit: ${error.message}`);
            if (/login/i.test(error.message)) fetchAnimeAccount().then(setAccount);
        }
    };
    const favoriteOf = (series) => (account.user && series
        ? { active: favoriteIds.includes(series.id), onToggle: () => toggleFavorite(series) }
        : null);
    const onLogout = async () => {
        setAccountMenu(null);
        await animeLogout().catch(() => {});
        setAccount(await fetchAnimeAccount());
        setNotice('Sudah keluar.');
    };
    const onDeleteAccount = async () => {
        setAccountMenu(null);
        if (!window.confirm('Hapus akun? Semua anime favorit ikut terhapus dan tidak bisa dikembalikan. Kamu tetap bisa menonton tanpa akun.')) return;
        try {
            await deleteAnimeAccount();
            setAccount(await fetchAnimeAccount());
            setNotice('Akun dan semua datanya sudah dihapus.');
        } catch (error) {
            setNotice(`Gagal menghapus akun: ${error.message}`);
        }
    };

    // ---------- halaman publik: daftar anime dari bot, tanpa login ----------
    useEffect(() => {
        if (!publicMode) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const library = await PublicLibrary.load();
                await loadProgress(library.userId);
                if (!cancelled) {
                    setServerMap(library.anilist);
                    setFileManager(library);
                }
            } catch (error) {
                if (!cancelled) setLoadError(error.message || String(error));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [publicMode]);

    // ---------- muat akun & daftar file (sama seperti halaman utama) ----------
    useEffect(() => {
        if (publicMode || !allowed) return undefined;
        setPublicMode(null);
        onAuthFailure(() => {
            clearLocalSession();
            navigate('/setup?expired=1');
        });
        if (!hasValidSession()) {
            navigate('/setup?relogin=1', { replace: true });
            return undefined;
        }
        let cancelled = false;
        let unsubscribe = null;
        (async () => {
            try {
                await refreshSessionIfNeeded().catch(() => {});
                const account = getAccount();
                const manager = await DisboxFileManager.create(account.webhookUrls);
                if (cancelled) return;
                unsubscribe = manager.subscribe((event) => {
                    if (event.type === 'tree-replaced') setTreeVersion((v) => v + 1);
                });
                await manager.syncFromDiscordIfNewer().catch((e) => console.warn('[Disbox] Sinkronisasi gagal:', e));
                await loadProgress(manager.userId);
                const map = await botJson(`/anime-map/${manager.userId}`).catch(() => ({ items: {} }));
                if (!cancelled) {
                    setServerMap(map.items || {});
                    setFileManager(manager);
                }
            } catch (error) {
                if (!cancelled) setLoadError(error.message || String(error));
            }
        })();
        return () => {
            cancelled = true;
            if (unsubscribe) unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const root = useMemo(() => (!fileManager ? null : publicMode ? fileManager.root : findAnimeRoot(fileManager)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fileManager, treeVersion, settingsOpen]);
    const library = useMemo(() => (fileManager && root ? buildLibrary(fileManager, root) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fileManager, root, treeVersion]);

    // ---------- info AniList ----------
    useEffect(() => {
        const state = { cancelled: false };
        const store = (id, info) => {
            if (!state.cancelled) setInfos((prev) => (prev[id] === info ? prev : { ...prev, [id]: info }));
        };
        library.forEach((series) => {
            fetchAnimeInfo(searchFor(series)).then((info) => store(series.id, info));
        });
        return () => {
            state.cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [library, infoVersion, serverMap]);

    // ---------- navigasi ----------
    const seriesId = params.get('s');
    const watchPath = params.get('w');
    const watching = useMemo(() => (watchPath ? findEpisode(library, watchPath) : null), [library, watchPath]);
    const openSeries = useMemo(() => (seriesId ? library.find((s) => matchesLink(seriesId, s.id)) : null), [library, seriesId]);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [seriesId, watchPath]);
    // Link lama (?w=/ANIME/...nama file) diganti kode pendek di address bar.
    useEffect(() => {
        if (watching && watchPath.startsWith('/')) setParams({ w: linkId(watching.episode.path) }, { replace: true });
        else if (!watchPath && openSeries && seriesId.startsWith('/')) setParams({ s: linkId(openSeries.id) }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watching, openSeries, watchPath, seriesId]);

    const viewTitle = watching ? `${episodeTitle(watching.episode)} · ${displayTitle(watching.series, infos[watching.series.id])}`
        : openSeries ? displayTitle(openSeries, infos[openSeries.id]) : 'Anime';
    useEffect(() => {
        document.title = `${viewTitle} | Disbox`;
        return () => {
            document.title = 'Disbox';
        };
    }, [viewTitle]);

    const goHome = () => setParams({});
    const goSeries = (series) => setParams({ s: linkId(series.id) });
    const play = (episode, series, { replace = false } = {}) => {
        setParams({ w: linkId(episode.path) }, { replace });
    };

    // ---------- tampilan ----------
    let content;
    if (!allowed) {
        content = (
            <CenterMessage>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Halaman Anime khusus akun admin.</Typography>
                <Button variant="contained" onClick={() => navigate('/')}>Kembali ke Disbox</Button>
            </CenterMessage>
        );
    } else if (loadError) {
        content = <CenterMessage><Alert severity="error">{publicMode ? 'Gagal memuat daftar anime' : 'Gagal memuat daftar file'}: {loadError}</Alert></CenterMessage>;
    } else if (!fileManager) {
        content = <CenterMessage><CircularProgress /><Typography color="text.secondary" sx={{ mt: 2 }}>Memuat pustaka anime...</Typography></CenterMessage>;
    } else if (!root && publicMode) {
        content = <CenterMessage><Typography>Belum ada anime.</Typography></CenterMessage>;
    } else if (!root) {
        content = (
            <CenterMessage>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Folder anime belum ditemukan.</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>Buat folder bernama "Anime" di File Saya, atau pilih foldernya di Setelan.</Typography>
                <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>Pilih folder</Button>
            </CenterMessage>
        );
    } else if (watchPath) {
        content = watching
            ? <WatchView fileManager={fileManager} series={watching.series} episode={watching.episode} info={infos[watching.series.id]} onPlay={play} onOpenSeries={goSeries} isDesktop={isDesktop} allowDownload={!publicMode} favorite={favoriteOf(watching.series)} />
            : <CenterMessage><Typography sx={{ mb: 2 }}>Episode ini tidak ditemukan (mungkin sudah dipindah atau dihapus).</Typography><Button variant="contained" onClick={goHome}>Ke beranda Anime</Button></CenterMessage>;
    } else if (seriesId) {
        content = openSeries
            ? <SeriesView key={openSeries.id} fileManager={fileManager} series={openSeries} info={infos[openSeries.id]} onPlay={play} onEditInfo={publicMode ? null : () => setEditing(openSeries)} favorite={favoriteOf(openSeries)} />
            : <CenterMessage><Typography sx={{ mb: 2 }}>Anime ini tidak ditemukan.</Typography><Button variant="contained" onClick={goHome}>Ke beranda Anime</Button></CenterMessage>;
    } else {
        content = library.length === 0
            ? <CenterMessage><Typography>Belum ada video di folder {root}.</Typography></CenterMessage>
            : <HomeView fileManager={fileManager} library={library} infos={infos} query={query} onOpenSeries={goSeries} onPlay={play}
                favoriteOf={favoriteOf} favoriteSeries={account.user ? favoriteIds.map((id) => library.find((s) => s.id === id)).filter(Boolean) : []} />;
    }

    const inSubPage = Boolean(seriesId || watchPath);
    const showSearch = !inSubPage && (isDesktop || searchOpen);
    const transparentBar = !watchPath && !query && fileManager && root && library.length > 0;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
                <AppBar
                    position={transparentBar ? 'absolute' : 'sticky'}
                    elevation={0}
                    sx={{ bgcolor: transparentBar ? 'transparent' : 'rgba(14,14,18,.95)', backgroundImage: transparentBar ? 'linear-gradient(rgba(0,0,0,.7), transparent)' : 'none', backdropFilter: transparentBar ? 'none' : 'blur(8px)' }}
                >
                    <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 }, px: { xs: 1, md: 3 } }}>
                        {inSubPage ? (
                            <IconButton color="inherit" onClick={() => (window.history.length > 1 ? navigate(-1) : goHome())} aria-label="Kembali"><ArrowBackIcon /></IconButton>
                        ) : null}
                        {!(searchOpen && !isDesktop) && (
                            <Box onClick={goHome} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 1 }}>
                                <LiveTvIcon sx={{ color: ACCENT }} />
                                <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.3 }}>
                                    Disbox<Box component="span" sx={{ color: ACCENT }}>Anime</Box>
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                        {showSearch && fileManager && root && (
                            <TextField
                                size="small"
                                placeholder="Cari anime..."
                                value={query}
                                autoFocus={!isDesktop}
                                onChange={(e) => setQuery(e.target.value)}
                                inputProps={{ name: 'disbox-anime-search', autoComplete: 'off', 'data-lpignore': 'true' }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                    endAdornment: (query || !isDesktop) ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => { setQuery(''); setSearchOpen(false); }}><CloseIcon fontSize="small" /></IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{ flex: isDesktop ? '0 1 320px' : 1, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,.45)' } }}
                            />
                        )}
                        {!showSearch && !inSubPage && fileManager && root && (
                            <IconButton color="inherit" onClick={() => setSearchOpen(true)} aria-label="Cari"><SearchIcon /></IconButton>
                        )}
                        {!(searchOpen && !isDesktop) && (
                            <Tooltip title={publicMode ? 'Kirim anime untuk ditayangkan' : 'Tinjau kontribusi anime'}>
                                {isDesktop
                                    ? <Button color="inherit" startIcon={<VolunteerActivismIcon />} onClick={() => navigate(publicMode ? '/kontribusi' : '/kontribusi-admin')}>Kontribusi</Button>
                                    : <IconButton color="inherit" onClick={() => navigate(publicMode ? '/kontribusi' : '/kontribusi-admin')}><VolunteerActivismIcon /></IconButton>}
                            </Tooltip>
                        )}
                        {publicMode && account.canLogin && !(searchOpen && !isDesktop) && (account.user ? (
                            <Tooltip title={account.user.name}>
                                <IconButton onClick={(e) => setAccountMenu(e.currentTarget)} sx={{ p: 0.5 }}>
                                    <Avatar src={account.user.avatar || undefined} alt="" sx={{ width: 32, height: 32, bgcolor: ACCENT, color: '#000', fontWeight: 800 }}>
                                        {account.user.name.slice(0, 1).toUpperCase()}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Masuk untuk menyimpan anime favorit (opsional)">
                                {isDesktop
                                    ? <Button color="inherit" startIcon={<LoginIcon />} onClick={() => setLoginOpen(true)}>Masuk</Button>
                                    : <IconButton color="inherit" onClick={() => setLoginOpen(true)}><LoginIcon /></IconButton>}
                            </Tooltip>
                        ))}
                        {!publicMode && fileManager && !(searchOpen && !isDesktop) && (
                            <Tooltip title="Setelan Anime">
                                <IconButton color="inherit" onClick={() => setSettingsOpen(true)}><SettingsIcon /></IconButton>
                            </Tooltip>
                        )}
                        {!publicMode && !(searchOpen && !isDesktop) && (
                            <Tooltip title="Kembali ke File Saya">
                                {isDesktop
                                    ? <Button color="inherit" startIcon={<FolderIcon />} onClick={() => navigate('/')}>File Saya</Button>
                                    : <IconButton color="inherit" onClick={() => navigate('/')}><FolderIcon /></IconButton>}
                            </Tooltip>
                        )}
                    </Toolbar>
                </AppBar>
                {content}
            </Box>
            {editing && (
                <InfoDialog series={editing} info={infos[editing.id]} initial={searchFor(editing)} onClose={() => setEditing(null)}
                    onSave={async (value) => {
                        setSearchOverride(editing.id, value);
                        clearAnimeInfoCache(value || editing.title);
                        try {
                            const saved = await botJson(`/anime-map/${fileManager.userId}`, { method: 'PUT', json: { path: editing.id, value } });
                            setServerMap(saved.items || {});
                        } catch (error) {
                            alert(`Tersimpan di browser ini saja, gagal menyimpan ke bot: ${error.message}`);
                        }
                        setEditing(null);
                        setInfos({});
                        setInfoVersion((v) => v + 1);
                    }} />
            )}
            <Menu anchorEl={accountMenu} open={Boolean(accountMenu)} onClose={() => setAccountMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                {account.user && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>{account.user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{favoriteIds.length} anime favorit</Typography>
                    </Box>
                )}
                <Divider />
                <MenuItem onClick={() => { setAccountMenu(null); goHome(); }}>
                    <ListItemIcon><FavoriteIcon fontSize="small" sx={{ color: '#ff4d6d' }} /></ListItemIcon>Favorit saya
                </MenuItem>
                <MenuItem onClick={onLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Keluar
                </MenuItem>
                <MenuItem onClick={onDeleteAccount} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>Hapus akun
                </MenuItem>
            </Menu>
            <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Masuk ke DisboxAnime</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Akun bersifat opsional. Tanpa akun kamu tetap bisa menonton semua anime. Dengan akun, kamu bisa menyimpan anime
                        favorit dan membukanya lagi dari perangkat mana pun.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Masuk memakai akun Discord, tanpa email dan tanpa password baru. Yang kami terima hanya nama, foto profil, dan ID
                        Discord-mu; pesan dan server Discord-mu tidak bisa dibaca. Akun bisa dihapus kapan saja dari menu profil.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={() => setLoginOpen(false)}>Nanti saja</Button>
                    <Button variant="contained" sx={{ bgcolor: '#5865F2', color: '#fff', '&:hover': { bgcolor: '#4752c4' } }}
                        onClick={() => { window.location.href = animeLoginUrl(); }}>
                        Masuk dengan Discord
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice(null)} message={notice}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
            {!publicMode && settingsOpen && fileManager && (
                <SettingsDialogAnime fileManager={fileManager} root={root} onClose={() => setSettingsOpen(false)}
                    onChanged={() => { setSettingsOpen(false); setInfos({}); setInfoVersion((v) => v + 1); setTreeVersion((v) => v + 1); }} />
            )}
        </ThemeProvider>
    );
}

function CenterMessage({ children }) {
    return (
        <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 3, pt: 8 }}>
            {children}
        </Box>
    );
}
